"use client";

/**
 * AI chat message bubble with avatar.
 *
 * Extracted from the prototype `compass-intake.jsx:75` (the `Ai` / `.cs-aimsg`, `.cs-ai` classes).
 * Renders the system avatar alongside a left-aligned message bubble.
 */

import type { ReactNode } from "react";
import { Avatar } from "./Avatar";

export interface AiBubbleProps {
  /** When true, the bubble stretches wider (e.g. for complex interactive content). */
  wide?: boolean;
  /** Message content — can be rich React content. */
  children: ReactNode;
}

/**
 * AI-side chat message: avatar on the left, content with no background bubble.
 *
 * Design change (Jun 18): AI messages are rendered without a white card —
 * the content sits directly on the canvas background with just the avatar
 * anchoring the left edge.
 *
 * `.cs-aimsg` → flex row, gap-3, items-start, animate slide-in
 * `.cs-ai`    → no bg/border/shadow — plain text on canvas
 */
export function AiBubble({ wide = false, children }: AiBubbleProps) {
  return (
    <div
      className={[
        "flex items-start gap-3",
        "animate-[fadeSlideIn_300ms_var(--ease)_both]",
        wide ? "max-w-full" : "max-w-[540px]",
      ].join(" ")}
    >
      <Avatar />
      <div
        className={[
          "flex-1 min-w-0",
          "text-[14px] leading-[1.55] text-(--fg-primary)",
        ].join(" ")}
      >
        {children}
      </div>
    </div>
  );
}
